import React, { useState } from 'react';
import { format } from 'date-fns';
import Icon from '../../../components/AppIcon';
import auditLogService from '../../../services/auditLogService';

const AuditLogEntry = ({ log }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const severity = auditLogService?.getSeverityLevel(log?.action, log?.tableName);
  const description = auditLogService?.formatChangeDescription(log);

  const severityConfig = {
    critical: {
      bg: 'bg-red-500/10',
      border: 'border-red-500/30',
      text: 'text-red-600',
      icon: 'AlertTriangle',
      label: 'Crítico'
    },
    high: {
      bg: 'bg-orange-500/10',
      border: 'border-orange-500/30',
      text: 'text-orange-600',
      icon: 'AlertCircle',
      label: 'Alto'
    },
    medium: {
      bg: 'bg-yellow-500/10',
      border: 'border-yellow-500/30',
      text: 'text-yellow-600',
      icon: 'Info',
      label: 'Medio'
    },
    low: {
      bg: 'bg-blue-500/10',
      border: 'border-blue-500/30',
      text: 'text-blue-600',
      icon: 'CheckCircle',
      label: 'Bajo'
    }
  };

  const config = severityConfig?.[severity] || severityConfig?.low;

  const actionIcons = {
    create: 'Plus',
    update: 'Edit',
    delete: 'Trash2',
    status_change: 'RefreshCw',
    approval: 'CheckCircle',
    rejection: 'XCircle'
  };

  const entityLabels = {
    work_orders: 'Orden de Trabajo',
    quotation_items: 'Cotización',
    clients: 'Cliente',
    vehicles: 'Vehículo',
    technical_inspections: 'Inspección',
    timeline_events: 'Evento',
    user_profiles: 'Usuario'
  };

  const formatTimestamp = (timestamp) => {
    try {
      return format(new Date(timestamp), 'dd/MM/yyyy HH:mm:ss');
    } catch {
      return timestamp;
    }
  };

  const renderChanges = () => {
    if (!log?.oldData && !log?.newData) return null;

    const oldData = log?.oldData || {};
    const newData = log?.newData || {};
    const allKeys = [...new Set([...Object.keys(oldData), ...Object.keys(newData)])];

    const changes = allKeys?.filter(key => 
      JSON.stringify(oldData?.[key]) !== JSON.stringify(newData?.[key])
    );

    if (changes?.length === 0) return null;

    return (
      <div className="mt-4 space-y-2">
        <h4 className="text-sm font-semibold text-foreground">Cambios Detectados:</h4>
        <div className="space-y-2">
          {changes?.map((key) => (
            <div key={key} className="bg-muted/50 rounded-lg p-3">
              <div className="text-xs font-medium text-muted-foreground mb-1">
                {key?.replace(/_/g, ' ')?.toUpperCase()}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <span className="text-xs text-muted-foreground">Anterior:</span>
                  <div className="text-sm text-foreground mt-1">
                    {JSON.stringify(oldData?.[key]) || 'N/A'}
                  </div>
                </div>
                <div>
                  <span className="text-xs text-muted-foreground">Nuevo:</span>
                  <div className="text-sm text-foreground mt-1">
                    {JSON.stringify(newData?.[key]) || 'N/A'}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className={`bg-card border rounded-lg overflow-hidden transition-smooth hover:shadow-md ${config?.border}`}>
      <div 
        className="p-4 cursor-pointer"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-start gap-4">
          {/* Severity Indicator */}
          <div className={`p-2 rounded-lg ${config?.bg}`}>
            <Icon name={config?.icon} size={20} className={config?.text} />
          </div>

          {/* Main Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-4 mb-2">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <Icon 
                    name={actionIcons?.[log?.action] || 'Activity'} 
                    size={16} 
                    className="text-muted-foreground" 
                  />
                  <span className="text-sm font-semibold text-foreground">
                    {entityLabels?.[log?.tableName] || log?.tableName}
                  </span>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${config?.bg} ${config?.text}`}>
                    {config?.label}
                  </span>
                </div>
                <p className="text-sm text-foreground">{description}</p>
              </div>
              
              <Icon 
                name={isExpanded ? 'ChevronUp' : 'ChevronDown'} 
                size={20} 
                className="text-muted-foreground flex-shrink-0" 
              />
            </div>

            {/* Metadata */}
            <div className="flex flex-wrap items-center gap-4 text-xs text-muted-foreground">
              <div className="flex items-center gap-1">
                <Icon name="User" size={14} />
                <span>{log?.user?.fullName || 'Sistema'}</span>
              </div>
              <div className="flex items-center gap-1">
                <Icon name="Clock" size={14} />
                <span>{formatTimestamp(log?.timestamp)}</span>
              </div>
              {log?.ipAddress && (
                <div className="flex items-center gap-1">
                  <Icon name="Globe" size={14} />
                  <span>{log?.ipAddress}</span>
                </div>
              )}
              <div className="flex items-center gap-1">
                <Icon name="Hash" size={14} />
                <span className="font-mono">{log?.recordId?.slice(0, 8)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Expanded Details */}
      {isExpanded && (
        <div className="border-t border-border p-4 bg-muted/30">
          {renderChanges()}
          
          {/* Additional Metadata */}
          <div className="mt-4 grid grid-cols-2 gap-4">
            <div>
              <span className="text-xs font-medium text-muted-foreground">ID de Registro:</span>
              <div className="text-sm text-foreground font-mono mt-1">{log?.recordId}</div>
            </div>
            {log?.userAgent && (
              <div>
                <span className="text-xs font-medium text-muted-foreground">User Agent:</span>
                <div className="text-sm text-foreground mt-1 truncate">{log?.userAgent}</div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AuditLogEntry;